import React, { useState, useEffect } from 'react';
import { Box, FormGroup, FormControlLabel, Checkbox, Typography, CircularProgress, Button, Alert } from '@mui/material';
import { getCatalogue } from '../../api/common/catalogues.ts';
import { submitQualityControls, getQualityControlsByFinishedGoodId } from '../../api/finishedGoods';
import { useSnack } from '../../services/SnakbarProvider';

const QualityControlView = ({ props }) => {
  const { id: finishedGoodId } = props || {};
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [qualityControls, setQualityControls] = useState([]);
  const [existingControls, setExistingControls] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const { openSnack } = useSnack();

  useEffect(() => {
    const fetchData = async () => {
      if (!finishedGoodId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch available quality controls
        const controlsResponse = await getCatalogue({ CatalogueName: 'QUALITY_CONTROL' });
        setQualityControls(controlsResponse.data);

        // Fetch existing quality controls for this finished good
        const existingResponse = await getQualityControlsByFinishedGoodId(finishedGoodId);
        setExistingControls(existingResponse.data);

        // Initialize checkboxes - mark existing ones as checked
        const initialCheckedState = controlsResponse.data.reduce((acc, item) => {
          const isExisting = existingResponse.data.some(existing => existing.controlId === item.id);
          acc[item.code] = isExisting;
          return acc;
        }, {});
        setCheckedItems(initialCheckedState);
      } catch (error) {
        console.error('Error fetching quality controls:', error);
        openSnack('Error al cargar los controles de calidad', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [finishedGoodId, openSnack]);

  const handleCheckboxChange = (code) => {
    setCheckedItems(prev => ({
      ...prev,
      [code]: !prev[code]
    }));
  };

  const handleSubmit = async () => {
    if (!finishedGoodId) {
      openSnack('Se requiere el ID del producto terminado', 'error');
      return;
    }

    // Get the IDs of checked controls
    const selectedControlIds = qualityControls
      .filter(control => checkedItems[control.code])
      .map(control => control.id);

    if (selectedControlIds.length === 0) {
      openSnack('Por favor seleccione al menos un elemento de control de calidad', 'warning');
      return;
    }

    setSubmitting(true);
    
    try {
      await submitQualityControls(finishedGoodId, selectedControlIds);
      openSnack('¡Controles de calidad enviados exitosamente!', 'success');
      
      // Refresh existing controls
      const existingResponse = await getQualityControlsByFinishedGoodId(finishedGoodId);
      setExistingControls(existingResponse.data);
    } catch (error) {
      console.error('Error submitting quality controls:', error);
      openSnack('Error al enviar los controles de calidad. Por favor intente nuevamente.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (!finishedGoodId) {
    return (
      <Box p={2}>
        <Alert severity="error">No se proporcionó ID del producto terminado</Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        Lista de Verificación de Control de Calidad
      </Typography>

      {existingControls.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {existingControls.length} elemento(s) de control de calidad ya enviado(s) para este producto terminado.
        </Alert>
      )}

      <FormGroup>
        {qualityControls.map((control) => (
          <FormControlLabel
            key={control.code}
            control={
              <Checkbox
                checked={checkedItems[control.code] || false}
                onChange={() => handleCheckboxChange(control.code)}
              />
            }
            label={control.name}
          />
        ))}
      </FormGroup>
      
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Enviando...' : 'Guardar controles de calidad'}
        </Button>
      </Box>
    </Box>
  );
};

export default QualityControlView; 